import { type ReactElement, Suspense, lazy } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { ModalHeader } from 'syf-component-library/ui/patterns/SlideInModalPortal'

import Loader from 'authorization-module/lib/ui/atoms/Loader'
import { cardActivationCacheKey } from '@syf-api-library/card-activation'
import routes from 'const/routes'
import getMFEConfig from 'helpers/getMFEConfig'
import i18n from 'strings/i18n'
import triggerAnalyticsEvent from 'helpers/triggerAnalyticsEvent'
import { selectedAccountAtom } from 'store/atoms'
import { getBrandRefreshPhase } from 'helpers/getBrandRefreshPhase'
import { SlideInModalPortalWrapper } from 'ui/patterns/SlideInModalPortalWrapper'

const CardActivationMFE = lazy(
  () => import('@card-actions/post-login-activation')
)

const CardActivationModal = (): ReactElement => {
  const selectedAccount = useAtomValue(selectedAccountAtom)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const title = i18n({ button: 'activateACard' }) as string

  const handleCloseModal = () => {
    triggerAnalyticsEvent({
      pageFunction: 'account services',
      pageKind: 'account services'
    })
    document.getElementById('activate-a-card-link')?.focus()
    // clear cache
    queryClient.removeQueries({
      queryKey: cardActivationCacheKey({
        accountId: selectedAccount,
        accountIdType: 'guid'
      })
    })
    navigate(routes.ACCOUNT_SERVICES)
  }

  const mfeConfig = getMFEConfig('mfe')

  return (
    <SlideInModalPortalWrapper
      ariaLabel={title}
      containerId="card-activation"
      escapeModalHandler={handleCloseModal}
      header={
        <ModalHeader
          closeModal={handleCloseModal}
          content={title}
          data-cy="activate-a-card-drawer-name"
          data-test="activate-a-card-modal-header"
          dataReason="x button"
          dataType="activate a card"
        />
      }
      id={`card-activation-modal-${selectedAccount}`}
      isOpen
      modalTriggerId="activate-a-card-link"
    >
      <Suspense fallback={<Loader />}>
        <CardActivationMFE
          featureFlags={{ brandRefreshPhase: getBrandRefreshPhase() }}
          mfeConfig={mfeConfig}
          onCloseModal={handleCloseModal}
          selectedAccountId={selectedAccount}
          triggerAnalyticsEvent={triggerAnalyticsEvent}
        />
      </Suspense>
    </SlideInModalPortalWrapper>
  )
}

export default CardActivationModal
